/**
 * web/src/games/nocturne/net.ts — Nocturne's transport over the couple room.
 *
 * A thin session on top of the SAME /ws endpoint and credentials the thread
 * uses (web/src/hooks/useThread.ts): no pair codes, no invite URLs, no local
 * credential store — the couple token from the existing session opens the
 * socket and the room validates it exactly as it does for the thread.
 *
 * The room treats `nocturne` frames as a pipe: they are relayed to the
 * partner socket and never stored. This client opens its own socket beside the
 * thread's (the room supports several sockets per seat), reconnects with
 * backoff, and re-asserts `{ kind: 'peer_online' }` awareness into the local
 * store on every (re)connect so a Case Host re-broadcasts public state.
 */
import { useEffect, useState } from 'react';
import { getToken, wsUrl } from '../../lib/api';
import { session } from '../../shell/bridge';
import type { ClientMessage, NocturneFrame } from '../../../../shared/types';
import type { DayAction, Difficulty, Mode, PublicState, Seat } from './engine';

/* ── Game-layer frames (carried inside { v: 1, t: 'nocturne', frame }) ── */

export type NocturneGameFrame =
  | { kind: 'new_case'; mode: Mode; difficulty: Difficulty; by: Seat }
  | { kind: 'intent'; action: DayAction; loc?: number; by: Seat }
  | { kind: 'public_state'; state: PublicState }
  /* Presence. `local: true` marks the synthetic frame this client dispatches
   * into its own store on every successful (re)connect; the server emits the
   * same kind to the partner when a seat connects. */
  | { kind: 'peer_online'; local?: boolean }
  | { kind: 'peer_offline' };

export type NocturneStatus = 'connecting' | 'live' | 'waiting-for-partner' | 'offline';

export interface NocturneSession {
  readonly status: NocturneStatus;
  readonly peerOnline: boolean;
  readonly seat: Seat | null;
  send(frame: NocturneGameFrame): void;
  /** Subscribe to incoming game frames (including server presence frames).
   *  Returns the unsubscribe function. */
  subscribe(cb: (frame: NocturneGameFrame) => void): () => void;
  close(): void;
}

/* ── frame validation (the room is a pipe; the game layer checks shape) ── */

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function parseGameFrame(raw: NocturneFrame): NocturneGameFrame | null {
  switch (raw.kind) {
    case 'new_case': {
      if (raw.mode !== 'duel' && raw.mode !== 'duet') return null;
      if (raw.difficulty !== 'first' && raw.difficulty !== 'city') return null;
      if (raw.by !== 'A' && raw.by !== 'B') return null;
      return { kind: 'new_case', mode: raw.mode, difficulty: raw.difficulty, by: raw.by };
    }
    case 'intent': {
      const action = raw.action;
      if (
        action !== 'canvass' && action !== 'interrogate' && action !== 'guard' &&
        action !== 'accuse' && action !== 'end_day'
      ) return null;
      if (raw.by !== 'A' && raw.by !== 'B') return null;
      const loc = raw.loc;
      const frame: NocturneGameFrame = { kind: 'intent', action, by: raw.by };
      if (loc !== undefined) {
        if (typeof loc !== 'number' || !Number.isInteger(loc) || loc < 0 || loc > 8) return null;
        (frame as { loc?: number }).loc = loc;
      }
      return frame;
    }
    case 'public_state': {
      if (!isRecord(raw.state) || raw.state.v !== 1) return null;
      return { kind: 'public_state', state: raw.state as unknown as PublicState };
    }
    case 'peer_online':
      return { kind: 'peer_online', local: raw.local === true ? true : undefined };
    case 'peer_offline':
      return { kind: 'peer_offline' };
    default:
      return null;
  }
}

/* ── the session ─────────────────────────────────────────────────────── */

const BACKOFF_MS = [1000, 2000, 4000, 8000, 10000];

class NocturneNet {
  private ws: WebSocket | null = null;
  private retryTimer: number | null = null;
  private attempt = 0;
  private stopped = false;
  private badToken = false;
  private open = false;
  private peerIsOnline = false;
  private frameListeners = new Set<(f: NocturneGameFrame) => void>();
  private statusListeners = new Set<() => void>();

  get status(): NocturneStatus {
    if (this.badToken || !getToken()) return 'offline';
    if (!this.open) return 'connecting';
    return this.peerIsOnline ? 'live' : 'waiting-for-partner';
  }

  get seat(): Seat | null {
    return session()?.role ?? null;
  }

  get peerOnline(): boolean {
    return this.peerIsOnline;
  }

  subscribe(cb: (f: NocturneGameFrame) => void): () => void {
    this.frameListeners.add(cb);
    return () => this.frameListeners.delete(cb);
  }

  onStatus(cb: () => void): () => void {
    this.statusListeners.add(cb);
    return () => this.statusListeners.delete(cb);
  }

  private emitStatus(): void {
    for (const cb of this.statusListeners) cb();
  }

  private emitFrame(f: NocturneGameFrame): void {
    for (const cb of this.frameListeners) cb(f);
  }

  send(frame: NocturneGameFrame): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.open) return;
    try {
      const msg: ClientMessage = { v: 1, t: 'nocturne', frame, to: 'partner' };
      this.ws.send(JSON.stringify(msg));
    } catch {
      /* socket died between the check and the send — the game is transient,
         the next frame after reconnect carries the state again */
    }
  }

  start(): void {
    this.stopped = false;
    this.badToken = false;
    this.connect();
  }

  close(): void {
    this.stopped = true;
    if (this.retryTimer !== null) {
      window.clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
    this.open = false;
    this.peerIsOnline = false;
    this.emitStatus();
  }

  private connect(): void {
    if (this.stopped) return;
    const token = getToken();
    if (!token) {
      this.emitStatus();
      return;
    }
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;
    const ws = new WebSocket(wsUrl(token));
    this.ws = ws;
    this.open = false;

    ws.onopen = () => {
      ws.send(JSON.stringify({ v: 1, t: 'hello', token }));
    };

    ws.onmessage = (ev: MessageEvent) => {
      let msg: unknown;
      try {
        msg = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
      } catch {
        return;
      }
      if (!isRecord(msg)) return;
      if (msg.t === 'welcome') {
        this.open = true;
        this.attempt = 0;
        // Re-assert peer_online awareness into the LOCAL store: if this device
        // is the Case Host, the panel re-broadcasts the current public state
        // on this frame, which is the whole reconnect path.
        this.emitFrame({ kind: 'peer_online', local: true });
        this.emitStatus();
        return;
      }
      if (msg.t === 'error') {
        const code = typeof msg.code === 'string' ? msg.code : '';
        if (code === 'bad_token') {
          // A retry can never fix a refused token — go offline for good.
          this.badToken = true;
          this.stopped = true;
          try { ws.close(); } catch { /* already closed */ }
          this.emitStatus();
        }
        return;
      }
      if (msg.t === 'nocturne' && isRecord(msg.frame)) {
        const parsed = parseGameFrame(msg.frame as NocturneFrame);
        if (!parsed) return;
        if (parsed.kind === 'peer_online' && parsed.local !== true) this.peerIsOnline = true;
        if (parsed.kind === 'peer_offline') this.peerIsOnline = false;
        this.emitFrame(parsed);
        this.emitStatus();
      }
    };

    ws.onclose = () => {
      if (this.ws === ws) this.ws = null;
      this.open = false;
      this.peerIsOnline = false;
      this.emitStatus();
      if (this.stopped || this.badToken || !getToken()) return;
      const wait = BACKOFF_MS[Math.min(this.attempt, BACKOFF_MS.length - 1)];
      this.attempt += 1;
      this.retryTimer = window.setTimeout(() => {
        this.retryTimer = null;
        this.connect();
      }, wait);
    };
  }
}

/* ── React binding ───────────────────────────────────────────────────── */

export interface NocturneSessionHandle {
  status: NocturneStatus;
  peerOnline: boolean;
  seat: Seat | null;
  send(frame: NocturneGameFrame): void;
  subscribe(cb: (frame: NocturneGameFrame) => void): () => void;
}

/** One Nocturne session per mounted panel: opens the socket, reconnects with
 *  backoff, and exposes reactive status/presence. */
export function useNocturneSession(): NocturneSessionHandle {
  const [net] = useState(() => new NocturneNet());
  const [status, setStatus] = useState<NocturneStatus>(net.status);
  const [peerOnline, setPeerOnline] = useState<boolean>(net.peerOnline);
  const token = getToken();

  useEffect(() => {
    net.start();
    const offStatus = net.onStatus(() => {
      setStatus(net.status);
      setPeerOnline(net.peerOnline);
    });
    setStatus(net.status);
    setPeerOnline(net.peerOnline);
    return () => {
      offStatus();
      net.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [net, token]);

  return {
    status,
    peerOnline,
    seat: net.seat,
    send: (frame) => net.send(frame),
    subscribe: (cb) => net.subscribe(cb),
  };
}
